import { type CSSProperties } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';

import { Alert, CircularProgress, Icon } from '@/ui';

export type MemberSessionTranscriptStatus =
  'error' | 'loading' | 'not-found' | 'ready';

export type MemberSessionTranscriptCopyState = 'copied' | 'error' | 'idle';

export interface MemberSessionTranscriptViewProps {
  copyState?: MemberSessionTranscriptCopyState;
  dateLabel?: string;
  errorMessage?: string;
  onBack?: () => void;
  onCopy?: () => void;
  privacyCoachName?: string;
  status: MemberSessionTranscriptStatus;
  transcriptBody?: string;
}

const font = "'Basis Grotesque', sans-serif";

const colors = {
  body: '#262626',
  divider: '#E3E3E3',
  muted: '#5C5C5C',
  pageBg: '#FEFBF7',
  sage: '#637A6F',
};

const pageStyles: CSSProperties = {
  background: colors.pageBg,
  color: colors.body,
  fontFamily: font,
  minHeight: '100vh',
};

const topBarStyles: CSSProperties = {
  alignItems: 'center',
  background: colors.pageBg,
  borderBottom: `1px solid ${colors.divider}`,
  boxSizing: 'border-box',
  display: 'flex',
  height: 72,
  justifyContent: 'space-between',
  padding: '24px',
  width: '100%',
};

const iconButtonStyles: CSSProperties = {
  alignItems: 'center',
  background: 'transparent',
  border: 0,
  borderRadius: 999,
  color: colors.body,
  cursor: 'pointer',
  display: 'inline-flex',
  height: 32,
  justifyContent: 'center',
  padding: 4,
  width: 32,
};

const disabledIconButtonStyles: CSSProperties = {
  ...iconButtonStyles,
  color: colors.muted,
  cursor: 'not-allowed',
  opacity: 0.5,
};

const shellStyles: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  padding: '50px 24px 80px',
};

const contentStyles: CSSProperties = {
  maxWidth: 672,
  width: '100%',
};

const eyebrowStyles: CSSProperties = {
  color: colors.sage,
  fontFamily: font,
  fontSize: 14,
  fontWeight: 500,
  letterSpacing: 0.17,
  lineHeight: 1.43,
  margin: '0 0 4px',
};

const titleStyles: CSSProperties = {
  color: colors.body,
  fontFamily: font,
  fontSize: 32,
  fontWeight: 700,
  lineHeight: 1.2,
  margin: '0 0 32px',
};

const privacyCalloutStyles: CSSProperties = {
  alignItems: 'flex-start',
  background: colors.pageBg,
  border: `1px solid ${colors.divider}`,
  borderRadius: 16,
  display: 'flex',
  gap: 16,
  margin: '0 0 32px',
  padding: 16,
};

const privacyTextStyles: CSSProperties = {
  color: colors.body,
  fontFamily: font,
  fontSize: 14,
  fontWeight: 400,
  letterSpacing: 0.17,
  lineHeight: 1.43,
  margin: 0,
};

const bodyStyles: CSSProperties = {
  color: colors.body,
  fontFamily: font,
  fontSize: 14,
  fontWeight: 500,
  letterSpacing: 0.17,
  lineHeight: 1.43,
  margin: 0,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const transcriptMarkdownStyles: CSSProperties = {
  color: colors.body,
  fontFamily: font,
  fontSize: 14,
  fontWeight: 500,
  letterSpacing: 0.17,
  lineHeight: 1.43,
  margin: 0,
  wordBreak: 'break-word',
};

const transcriptParagraphStyles: CSSProperties = {
  ...bodyStyles,
  margin: '0 0 16px',
};

const transcriptTimestampStyles: CSSProperties = {
  ...bodyStyles,
  fontWeight: 700,
  margin: '24px 0 16px',
};

const transcriptListStyles: CSSProperties = {
  ...bodyStyles,
  margin: '0 0 16px',
  paddingLeft: 24,
};

const transcriptListItemStyles: CSSProperties = {
  ...bodyStyles,
  margin: '0 0 8px',
};

const transcriptAnchorStyles: CSSProperties = {
  color: colors.body,
  textDecoration: 'underline',
};

const transcriptMarkdownComponents: Components = {
  a: ({ children, href }) => {
    if (typeof href !== 'string' || !href.trim()) {
      return <span>{children}</span>;
    }

    return (
      <a
        href={href}
        rel="noopener noreferrer"
        style={transcriptAnchorStyles}
        target="_blank"
      >
        {children}
      </a>
    );
  },
  h1: ({ children }) => <h1 style={transcriptTimestampStyles}>{children}</h1>,
  h2: ({ children }) => <h2 style={transcriptTimestampStyles}>{children}</h2>,
  h3: ({ children }) => <h3 style={transcriptTimestampStyles}>{children}</h3>,
  h4: ({ children }) => <h4 style={transcriptTimestampStyles}>{children}</h4>,
  h5: ({ children }) => <h5 style={transcriptTimestampStyles}>{children}</h5>,
  h6: ({ children }) => <h6 style={transcriptTimestampStyles}>{children}</h6>,
  img: () => null,
  li: ({ children }) => <li style={transcriptListItemStyles}>{children}</li>,
  ol: ({ children }) => <ol style={transcriptListStyles}>{children}</ol>,
  p: ({ children }) => <p style={transcriptParagraphStyles}>{children}</p>,
  ul: ({ children }) => <ul style={transcriptListStyles}>{children}</ul>,
};

const centeredStateStyles: CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  minHeight: 240,
};

const getCopyTooltip = (
  copyState: MemberSessionTranscriptCopyState,
  hasBody: boolean,
): string => {
  if (!hasBody) {
    return 'Transcript content is not ready yet.';
  }
  if (copyState === 'copied') {
    return 'Transcript copied';
  }
  if (copyState === 'error') {
    return 'Unable to copy transcript';
  }
  return 'Copy transcript';
};

export const MemberSessionTranscriptView = ({
  copyState = 'idle',
  dateLabel,
  errorMessage,
  onBack,
  onCopy,
  privacyCoachName,
  status,
  transcriptBody = '',
}: MemberSessionTranscriptViewProps) => {
  const trimmedBody = transcriptBody.trim();
  const hasBody = trimmedBody.length > 0;
  const privacyName = privacyCoachName?.trim() || 'your coach';
  const copyTooltip = getCopyTooltip(copyState, hasBody);

  const renderContent = () => {
    if (status === 'loading') {
      return (
        <div style={centeredStateStyles}>
          <CircularProgress aria-label="Loading transcript" />
        </div>
      );
    }

    if (status === 'not-found') {
      return <Alert severity="info">Transcript not found.</Alert>;
    }

    if (status === 'error') {
      return (
        <Alert severity="error">
          {errorMessage || 'Failed to load transcript. Please try again later.'}
        </Alert>
      );
    }

    if (!hasBody) {
      return (
        <Alert severity="info">Transcript content is not ready yet.</Alert>
      );
    }

    return (
      <div style={transcriptMarkdownStyles}>
        <ReactMarkdown components={transcriptMarkdownComponents}>
          {trimmedBody}
        </ReactMarkdown>
      </div>
    );
  };

  return (
    <div style={pageStyles}>
      <div style={topBarStyles}>
        <button
          aria-label="Go back"
          style={iconButtonStyles}
          type="button"
          onClick={onBack}
        >
          <Icon>arrow_back</Icon>
        </button>
        <button
          aria-label={copyTooltip}
          disabled={!hasBody}
          style={hasBody ? iconButtonStyles : disabledIconButtonStyles}
          title={copyTooltip}
          type="button"
          onClick={onCopy}
        >
          <Icon>content_copy</Icon>
        </button>
      </div>
      <main style={shellStyles}>
        <div style={contentStyles}>
          <p style={eyebrowStyles}>
            {dateLabel
              ? `${dateLabel} Session transcript`
              : 'Session transcript'}
          </p>
          <h1 style={titleStyles}>Transcript</h1>
          {status === 'ready' && (
            <div style={privacyCalloutStyles}>
              <Icon fontSize="small">lock</Icon>
              <p style={privacyTextStyles}>
                Only you and {privacyName} can see this. Transcripts aren't
                shared with your employer.
              </p>
            </div>
          )}
          {renderContent()}
        </div>
      </main>
    </div>
  );
};
